import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';
import type { TaskClientRef } from './tasksApi';

// ---------------------------------------------------------------------------
// Calendar de postări — mirrors backend/src/routes/socialPosts.ts.
//
// A post is a task (assignee, status, production deadline, estimate, comments
// — edited through tasksApi) plus its publishing side (platform, date and
// time, caption, media link, client approval — edited here).
// ---------------------------------------------------------------------------

export const SOCIAL_PLATFORMS = [
  'INSTAGRAM',
  'FACEBOOK',
  'TIKTOK',
  'LINKEDIN',
  'YOUTUBE',
  'X',
  'OTHER',
] as const;
export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];

export const SOCIAL_FORMATS = ['POST', 'CAROUSEL', 'REEL', 'STORY', 'VIDEO'] as const;
export type SocialPostFormat = (typeof SOCIAL_FORMATS)[number];

export const APPROVAL_STATUSES = [
  'DRAFT',
  'PENDING_CLIENT',
  'APPROVED',
  'CHANGES_REQUESTED',
] as const;
export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number];

export const PLATFORM_LABELS: Record<SocialPlatform, string> = {
  INSTAGRAM: 'Instagram',
  FACEBOOK: 'Facebook',
  TIKTOK: 'TikTok',
  LINKEDIN: 'LinkedIn',
  YOUTUBE: 'YouTube',
  X: 'X',
  OTHER: 'Altă platformă',
};

export const PLATFORM_COLORS: Record<SocialPlatform, string> = {
  INSTAGRAM: '#e1306c',
  FACEBOOK: '#1877f2',
  TIKTOK: '#111827',
  LINKEDIN: '#0a66c2',
  YOUTUBE: '#ff0000',
  X: '#374151',
  OTHER: '#6b7280',
};

export const FORMAT_LABELS: Record<SocialPostFormat, string> = {
  POST: 'Postare',
  CAROUSEL: 'Carusel',
  REEL: 'Reel',
  STORY: 'Story',
  VIDEO: 'Video',
};

export const APPROVAL_LABELS: Record<ApprovalStatus, string> = {
  DRAFT: 'Ciornă internă',
  PENDING_CLIENT: 'La client',
  APPROVED: 'Aprobată',
  CHANGES_REQUESTED: 'Modificări cerute',
};

export const APPROVAL_TONES: Record<ApprovalStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-600',
  PENDING_CLIENT: 'bg-amber-100 text-amber-800',
  APPROVED: 'bg-green-100 text-green-800',
  CHANGES_REQUESTED: 'bg-red-100 text-red-700',
};

export interface SocialPost {
  id: string;
  identifier: string | null;
  title: string;
  description: string | null;
  status: { id: string; name: string; slug: string; color: string; category: string };
  assignee: { id: string; name: string } | null;
  dueDate: string | null;
  estimate: number | null;
  commentCount: number;
  /** Null when the post sits on the client with no real project. */
  project: { id: string; name: string; code: string | null } | null;
  projectId: string;
  client: TaskClientRef | null;
  platform: SocialPlatform;
  format: SocialPostFormat;
  publishAt: string;
  caption: string | null;
  mediaUrl: string | null;
  approvalStatus: ApprovalStatus;
  approvalUpdatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ApprovalEvent {
  id: string;
  status: ApprovalStatus;
  actorName: string;
  actorUserId: string | null;
  comment: string | null;
  viaShareLink: boolean;
  createdAt: string;
}

export interface SocialPostDetail extends SocialPost {
  approvals: ApprovalEvent[];
}

export interface PostFilters {
  from: string;
  to: string;
  clientId?: string[];
  platform?: string[];
  approval?: string[];
  assigneeId?: string;
}

export interface CreatePostInput {
  title: string;
  description?: string;
  projectId?: string | null;
  clientId?: string | null;
  statusId?: string;
  assigneeId?: string | null;
  dueDate?: string | null;
  estimate?: number | null;
  platform: SocialPlatform;
  format?: SocialPostFormat;
  publishAt: string;
  caption?: string | null;
  mediaUrl?: string | null;
}

export type UpdatePostInput = Partial<
  Pick<CreatePostInput, 'platform' | 'format' | 'publishAt' | 'caption' | 'mediaUrl'>
>;

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function listPosts(
  filters: PostFilters,
): Promise<{ posts: SocialPost[]; capped: boolean }> {
  const params = new URLSearchParams({ from: filters.from, to: filters.to });
  if (filters.clientId?.length) params.set('clientId', filters.clientId.join(','));
  if (filters.platform?.length) params.set('platform', filters.platform.join(','));
  if (filters.approval?.length) params.set('approval', filters.approval.join(','));
  if (filters.assigneeId) params.set('assigneeId', filters.assigneeId);
  const res = await apiFetch<Envelope<{ posts: SocialPost[]; capped: boolean }>>(
    `/api/social-posts?${params.toString()}`,
    { headers: authHeaders() },
  );
  return res.data;
}

export async function getPost(taskId: string): Promise<SocialPostDetail> {
  const res = await apiFetch<Envelope<SocialPostDetail>>(`/api/social-posts/${taskId}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function createPost(input: CreatePostInput): Promise<SocialPost> {
  const res = await apiFetch<Envelope<SocialPost>>('/api/social-posts', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function updatePost(taskId: string, input: UpdatePostInput): Promise<SocialPost> {
  const res = await apiFetch<Envelope<SocialPost>>(`/api/social-posts/${taskId}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function setPostApproval(
  taskId: string,
  status: ApprovalStatus,
  comment?: string,
): Promise<SocialPost> {
  const res = await apiFetch<Envelope<SocialPost>>(`/api/social-posts/${taskId}/approval`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ status, comment }),
  });
  return res.data;
}

export async function duplicatePost(
  taskId: string,
  platform: SocialPlatform,
  publishAt?: string,
): Promise<SocialPost> {
  const res = await apiFetch<Envelope<SocialPost>>(`/api/social-posts/${taskId}/duplicate`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ platform, publishAt }),
  });
  return res.data;
}
